import React, { useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { LANDING_PATH, hasPrerenderedHead, releasePrerenderedHead } from '../utils/prerenderedHead';
import { getProductUrlSlug } from '../data/seoConfig';
import { attachMerchantListingFields } from '../data/merchantListingSchema';
import { resolveBlogImage, absoluteImageUrl } from '../utils/blogImage';

const SITE_URL = 'https://rcmbiosciences.com';
const SITE_NAME = 'RCM Biosciences';

const NOINDEX_PATHS = ['/admin', '/login', '/account', '/cart', '/checkout', '/thank-you', '/search', '/preview', '/order-history', '/wishlist', '/notification-settings', '/messages', '/partner-program', '/partner-dashboard', '/invoice', '/invoice-lookup', '/invoice-confirmation', '/register', '/auth', '/profile', '/orders'];

const SEO = ({ title, description, path, type = 'website', image, schema, noindex }) => {
  // Hook calls must be at the top of the component. BrowserRouter now wraps everything that uses SEO.
  const location = useLocation();
  const livePath = location.pathname || '/';
  const liveSearch = location.search || '';
  const actualPath = path || livePath;

  const fullTitle = title ? title + ' | ' + SITE_NAME : SITE_NAME + ' | Research Peptide & Bioscience Company';

  // Canonicals must NEVER include faceted / filter / pagination / tracking
  // query strings (sort, page, filter, ref, utm_*, etc.) — those produce
  // duplicate-content variants of the same page.
  // English-only mode (2026-07-18): `?lang=es` is now 301-redirected at
  // the edge, `<html lang="en">` is hard-coded, and no `hreflang="es"`
  // alternate is emitted.
  const cleanPath = actualPath.split('?')[0].replace(/\/$/, '') || '/';
  const pathOnly = cleanPath === '/' ? '/' : cleanPath;
  const enUrl = SITE_URL + pathOnly;

  const canonicalUrl = enUrl;
  const fullUrl = canonicalUrl;

  const ogImage = image || `${SITE_URL}/images/logo.png`;
  const descText = description || '';
  const shouldNoindex = noindex || NOINDEX_PATHS.some(p => cleanPath.startsWith(p)) || actualPath.includes('?product=');
  const isAdminPath = /^\/admin(\/|$)/.test(cleanPath);
  const robotsContent = isAdminPath ? 'noindex,nofollow' : shouldNoindex ? 'noindex,follow' : 'index,follow,max-image-preview:large';

  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  // On the landing page the prerendered <head> is authoritative; React only takes over after navigation.
  const usePrerendered = hasPrerenderedHead() && livePath === LANDING_PATH && !isAdminPath;
  useLayoutEffect(() => {
    if (!usePrerendered) releasePrerenderedHead();
  }, [usePrerendered]);

  if (usePrerendered) return null;

  return (
    <Helmet
      title={fullTitle}
      htmlAttributes={{ lang: 'en' }}
      meta={[
        { name: 'title', content: fullTitle },
        { name: 'description', content: descText },
        { name: 'robots', content: robotsContent },
        { name: 'googlebot', content: robotsContent },
        { property: 'og:type', content: type },
        { property: 'og:url', content: fullUrl },
        { property: 'og:title', content: fullTitle },
        { property: 'og:description', content: descText },
        { property: 'og:image', content: ogImage },
        { property: 'og:site_name', content: SITE_NAME },
        { property: 'og:locale', content: 'en_US' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:url', content: fullUrl },
        { name: 'twitter:title', content: fullTitle },
        { name: 'twitter:description', content: descText },
        { name: 'twitter:image', content: ogImage }
      ]}
      link={[
        { rel: 'canonical', href: canonicalUrl },
        // English-only site — no hreflang alternates emitted. Removing the
        // `es` + `x-default` entries eliminates the duplicate-content
        // signal that GSC previously flagged as "Duplicate, Google chose
        // different canonical than user".
      ]}
      script={schemas.map(s => ({ type: 'application/ld+json', innerHTML: JSON.stringify(s) }))}
    />
  );
};

export const SITE_URL_EXPORT = SITE_URL;

// Organization Schema
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "RCM Biosciences",
  "url": SITE_URL,
  "logo": `${SITE_URL}/images/logo.png`,
  "sameAs": []
};

// WebSite Schema
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "RCM Biosciences",
  "url": SITE_URL
};

// FAQ Schema builder
export const buildFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.q,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.a
    }
  }))
});

// Product Schema builder (optionally with aggregateRating + reviews + related articles)
export const buildProductSchema = (product, reviewStats = null, reviews = null, relatedArticles = null) => {
  // Canonical product URL slug. Always defers to product.slug first (set by
  // the seed_product_slugs migration), then to name+dosage for legacy
  // records — never to a different name-only formula that diverges from the
  // prerendered HTML and the sitemap.
  const slug = getProductUrlSlug(product);
  const isLabSupply = product.category === 'lab_supplies';
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "brand": { "@type": "Brand", "name": "RCM Biosciences" },
    "category": isLabSupply ? "Laboratory Research Supply" : "Research Peptide",
    "description": isLabSupply
      ? (product.shortDescription || product.description || `${product.name} — laboratory research supply. Research use only. Not for human or animal use. Not for injection.`)
      : `${product.name} listed in the RCM Biosciences catalog for laboratory and analytical research. Research use only.`,
    "url": `${SITE_URL}/products/${slug}`,
    "image": product.image ? [product.image] : [`${SITE_URL}/images/${slug}.jpg`]
  };
  
  // SKU
  if (product.catNo) schema.sku = product.catNo;
  
  // Offers — Availability + price MUST match the build-time PDP emitter
  // in scripts/prerender.js so Google never sees drift between the
  // prerendered HTML and the client-hydrated schema. See 2026-07-28 code
  // review finding "Product availability/schema drift across the three
  // JSON-LD emitters".
  if (product.price1) {
    schema.offers = {
      "@type": "Offer",
      "url": `${SITE_URL}/products/${slug}`,
      "priceCurrency": "USD",
      "price": Number(product.price1).toFixed(2),
      "availability": (product.inStock !== false && product.stock_count !== 0)
        ? "https://schema.org/InStock"
        : "https://schema.org/BackOrder",
      "itemCondition": "https://schema.org/NewCondition"
    };
    // Google Merchant Listings warning fix — attach the shared
    // shippingDetails + hasMerchantReturnPolicy blocks pinned to the
    // ACTUAL published RCM policies (see src/data/merchantListingSchema.js).
    attachMerchantListingFields(schema.offers);
  }
  
  // Aggregate rating (only if 1+ reviews — Google requires at least 1)
  if (reviewStats && reviewStats.total_reviews > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": reviewStats.average_rating,
      "reviewCount": reviewStats.total_reviews,
      "bestRating": 5,
      "worstRating": 1
    };
  }
  
  // Individual reviews (top 5)
  if (reviews && reviews.length > 0) {
    schema.review = reviews.slice(0, 5).map(r => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": r.name },
      "datePublished": r.created_at,
      "reviewBody": r.review,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": r.rating,
        "bestRating": 5,
        "worstRating": 1
      }
    }));
  }

  // subjectOf[] — cross-link this product to research articles that mention
  // it. Lets AI engines (and Google) follow the entity relationship from a
  // Product page back to the editorial coverage on the blog. Recommended
  // fields (author, publisher, image) are echoed here so the schema
  // validator doesn't flag them as missing — and every Organization node
  // carries the required `url` field per schema.org rules.
  if (Array.isArray(relatedArticles) && relatedArticles.length > 0) {
    const isOrgAuthor = (s) => typeof s === 'string' && /team|holdings|biosciences/i.test(s);
    schema.subjectOf = relatedArticles.map(a => {
      const authorIsOrg = isOrgAuthor(a.author);
      return {
        "@type": "Article",
        "headline": a.title,
        "url": `${SITE_URL}/blog/${a.slug}`,
        "datePublished": a.created_at,
        "dateModified": a.updated_at || a.created_at,
        "inLanguage": a.language || 'en',
        "author": authorIsOrg
          ? { "@type": "Organization", "name": a.author, "url": SITE_URL, "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo512.png` }, "contactPoint": { "@type": "ContactPoint", "email": "research@rcmbiosciences.com", "contactType": "Customer Support" } }
          : { "@type": "Person", "name": a.author || "RCM Biosciences Research Team", "url": `${SITE_URL}/about` },
        "publisher": {
          "@type": "Organization",
          "name": "RCM Biosciences",
          "url": SITE_URL,
          "logo": {
            "@type": "ImageObject",
            "url": `${SITE_URL}/logo512.png`,
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "email": "research@rcmbiosciences.com",
            "contactType": "Customer Support",
          },
        },
        "image": absoluteImageUrl(resolveBlogImage(a), SITE_URL),
      };
    });
  }

  return schema;
};

// BreadcrumbList Schema builder
export const buildBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": item.name,
    "item": `${SITE_URL}${item.path}`
  }))
});

// Product slug generator
export const productSlug = (name) => {
  if (!name) return '';
  return name.toLowerCase()
    .replace(/\(([^)]+)\)/g, '$1')
    .replace(/\+/g, '-')
    .replace(/[\s]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

export default SEO;
